import { FinalScoreValence } from '@upright/common/model/output/scores/drilldowns/types';
import { Leena2TopImpact } from '@upright/common/model/output/scores/drilldowns/leena2-top-impacts-types';
import { Valence } from '@upright/common/model/types';
import {
  getValueChainFill,
  valueChainSegments,
} from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-chain-fill';
import { Button } from '@upright/ui/common/generic/components/button';
import { Stack } from '@upright/ui/common/generic/components/layout';
import { Popover, PopoverTrigger } from '@upright/ui/common/generic/components/popover';
import { Text } from '@upright/ui/common/generic/components/typography';
import { toLeena2Interval } from '@upright/ui/common/upright/model/output/scores/leena2-uncertainty/format';
import { Leena2IntervalText } from '@upright/ui/common/upright/model/output/scores/leena2-uncertainty/interval-text';
import { useShowLeena2Uncertainty } from '@upright/ui/common/upright/model/output/scores/leena2-uncertainty/use-show-leena2-uncertainty';
import { useState } from 'react';
import styled from 'styled-components';
import { ResearchPreviewBadge } from '../leena2-research-preview-badge';
import { Leena2ImpactDetailsDialog } from './leena2-impact-details-dialog';
import { useLeena2CategoryTopImpacts, useLeena2ProductImpactTopics } from './use-leena2-category-top-impacts';

const VISIBLE_COUNT = 4;

/**
 * "Top impacts" list shown below the contributing-products list in the net-impact
 * category drill-in side panel, derived from Leena-2 per-leaf results aggregated
 * across the company's products weighted by revenue share. Team-only and behind the
 * `companyProduct.leena2TopImpactsInDrilldown` view setting — the caller (panel.tsx)
 * guards rendering with `useViewSetting(...)`, so this component assumes it is only
 * mounted for team users with the toggle on. It still self-hides on loading / error /
 * empty so it never disturbs the contributors list it sits under.
 *
 * Styled to mirror the contributing-products rows: an uppercase eyebrow title, then
 * `label | leader-normalised bar | value (c/$)` rows in the same grid + tokens.
 */
export function Leena2TopImpacts(props: {
  /** A company variant's products weighted by revenue share, or one product's own topics (PLT-7045). */
  source: { kind: 'company'; resultId: string | undefined } | { kind: 'product'; productCode: string };
  impactCode: string;
  valence: FinalScoreValence;
  /** Value-chain mode from the originating chart (PLT-5382). When on, each
   *  impact row's bar fill is split by value chain and the detail dialog shows
   *  the selected product's split. */
  showValueChains: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [selectedImpact, setSelectedImpact] = useState<Leena2TopImpact | null>(null);
  const { source } = props;
  // The team / view-setting gate is applied by the caller (panel.tsx), which only mounts this
  // component when it holds; each hook is enabled for its own source only.
  const companyQuery = useLeena2CategoryTopImpacts({
    resultId: source.kind === 'company' ? source.resultId : undefined,
    impactCode: props.impactCode,
    valence: props.valence,
    enabled: source.kind === 'company',
  });
  const productQuery = useLeena2ProductImpactTopics({
    productCode: source.kind === 'product' ? source.productCode : undefined,
    impactCode: props.impactCode,
    valence: props.valence,
    enabled: source.kind === 'product',
  });
  const query = source.kind === 'company' ? companyQuery : productQuery;
  const showUncertainty = useShowLeena2Uncertainty();

  // Hide the whole section on loading / error / no Leena-2 results.
  if (query.isLoading || query.error || !query.data) {
    return null;
  }

  // `productsByImpact` defaults to `{}` so an older backend (pre-rollout) that
  // doesn't send it yet degrades gracefully — clicking a row opens the dialog
  // with an empty product list instead of throwing — rather than crashing.
  const { topImpacts, productsWithLeena2, totalProducts, productsByImpact = {} } = query.data;
  if (topImpacts.length === 0) {
    return null;
  }

  const visible = expanded ? topImpacts : topImpacts.slice(0, VISIBLE_COUNT);
  const hasMore = topImpacts.length > VISIBLE_COUNT;
  const isSubset = productsWithLeena2.length < totalProducts;
  // topImpacts is sorted descending, so the first row is the leader the bars
  // normalise against (mirrors the contributing-products bar treatment).
  const leaderValue = topImpacts[0]?.value ?? 0;
  // Show each leaf's share of the category total as a percentage rather than an
  // absolute c/$ figure: these values come from the Leena-2 product model version,
  // so an absolute c/$ wouldn't line up with the panel's c/$ (a different version) —
  // a share-of-category is self-contained and doesn't invite that comparison.
  const totalValue = topImpacts.reduce((sum, impact) => sum + impact.value, 0);

  return (
    <Root>
      <TitleRow>
        <ListEyebrow>Top impacts</ListEyebrow>
        <ResearchPreviewBadge>Beta preview</ResearchPreviewBadge>
      </TitleRow>
      <ImpactList>
        {visible.map(impact => {
          const barPct = leaderValue > 0 ? (impact.value / leaderValue) * 100 : 0;
          const sharePct = totalValue > 0 ? (impact.value / totalValue) * 100 : 0;
          // Split the fill by value chain only when the chart's value-chain
          // mode is on and the row carries a genuine breakdown; otherwise keep
          // the plain valence bar. The bar's leader-normalised length is
          // unchanged — only the internal fill is divided.
          const segments = props.showValueChains ? valueChainSegments(impact.valueChain) : [];
          // Clef results with "Show Leena-2 uncertainty" on: the leaf value's
          // 80% interval, as a share of the same category total as the %.
          const valueInterval = showUncertainty ? toLeena2Interval(impact.valueP10, impact.valueP90) : undefined;
          const shareInterval =
            valueInterval && totalValue > 0
              ? { p10: (valueInterval.p10 / totalValue) * 100, p90: (valueInterval.p90 / totalValue) * 100 }
              : undefined;
          return (
            <ImpactRow
              key={impact.impactId}
              type="button"
              onClick={() => {
                setSelectedImpact(impact);
              }}
            >
              <ImpactLabel title={impact.description}>{impact.description}</ImpactLabel>
              <BarWrap>
                {segments.length > 0 ? (
                  <BarStack style={{ width: `${Math.min(100, barPct)}%` }}>
                    {segments.map(segment => (
                      <BarSegment
                        key={segment.valueChain}
                        style={{
                          width: `${segment.fraction * 100}%`,
                          background: getValueChainFill(segment.valueChain),
                        }}
                      />
                    ))}
                  </BarStack>
                ) : (
                  <Bar style={{ width: `${Math.min(100, barPct)}%` }} $valence={props.valence} />
                )}
              </BarWrap>
              {/* An invisible two-line sizer (share % + a c/$ placeholder) keeps
                  the row the same height as the contributing-products rows, which
                  do show c/$. The visible % is absolutely centred over that sizer
                  so it lines up vertically with the label and bar (rather than
                  sitting at the top of the reserved two-line box). */}
              {shareInterval ? (
                // With an interval, the reserved second line carries it.
                <ImpactFigures>
                  <FiguresStack>
                    <ImpactPct>{formatSharePct(sharePct)}</ImpactPct>
                    <ImpactCpsSpacer>
                      <Leena2IntervalText interval={shareInterval} format={formatShareNumber} prefix="" suffix="%" />
                    </ImpactCpsSpacer>
                  </FiguresStack>
                </ImpactFigures>
              ) : (
                <ImpactFigures>
                  <FiguresSizer aria-hidden="true">
                    <ImpactPct>{formatSharePct(sharePct)}</ImpactPct>
                    <ImpactCpsSpacer>&nbsp;</ImpactCpsSpacer>
                  </FiguresSizer>
                  <ImpactPctCentered>{formatSharePct(sharePct)}</ImpactPctCentered>
                </ImpactFigures>
              )}
            </ImpactRow>
          );
        })}
      </ImpactList>
      {(hasMore || isSubset) && (
        <FooterRow>
          {hasMore && (
            <ShowMore
              type="button"
              onClick={() => {
                setExpanded(value => !value);
              }}
            >
              {expanded ? 'Show less' : `Show ${topImpacts.length - VISIBLE_COUNT} more`}
            </ShowMore>
          )}
          {isSubset && (
            <SubsetBannerSlot>
              <SubsetBanner
                shownCount={productsWithLeena2.length}
                totalCount={totalProducts}
                products={productsWithLeena2}
              />
            </SubsetBannerSlot>
          )}
        </FooterRow>
      )}
      <Leena2ImpactDetailsDialog
        impact={selectedImpact}
        products={selectedImpact ? (productsByImpact[selectedImpact.impactId] ?? []) : []}
        valence={props.valence}
        onClose={() => {
          setSelectedImpact(null);
        }}
      />
    </Root>
  );
}

/** Leaf's share of the category total, formatted like the contributing-products
 *  share figure (1 decimal under 10%, whole numbers above). */
function formatSharePct(pct: number): string {
  return `${formatShareNumber(pct)}%`;
}

function formatShareNumber(pct: number): string {
  return pct < 10 ? pct.toFixed(1) : pct.toFixed(0);
}

function SubsetBanner(props: {
  shownCount: number;
  totalCount: number;
  products: Array<{ code: string; name: string }>;
}) {
  const [isOpen, setIsOpen] = useState<boolean>();
  return (
    <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button variant="text" size="small" color="primary">
        Based on {props.shownCount} of {props.totalCount} products
      </Button>
      <Popover>
        <Stack gap="sm" inlineSize="280px">
          <Text size="xs" color="gray-500">
            Products with Beta preview results
          </Text>
          {props.products.map(product => (
            <Text key={product.code} size="sm" color="gray-900">
              {product.code} — {product.name}
            </Text>
          ))}
        </Stack>
      </Popover>
    </PopoverTrigger>
  );
}

/* ─── styled (mirrors the contributing-products list in `tabs/contributors.tsx`) ─── */

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
`;

/* Title row: the eyebrow + the "research preview" badge, left-aligned. (The
   subset trigger now lives in the footer beneath the list.) */
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__sm);
  margin-top: var(--space__sm);
`;

const ListEyebrow = styled.div`
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  letter-spacing: 0.04em;
  color: var(--color__gray-600);
  text-transform: uppercase;
`;

const ImpactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
`;

/* Clickable row — opens the impact-detail dialog. Reset native button chrome
   so it reads as a list row, with a subtle hover affordance. */
const ImpactRow = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(80px, 30%) 64px;
  align-items: center;
  gap: var(--space__sm);
  font-size: var(--font-size__sm);
  width: 100%;
  margin: 0 calc(-1 * var(--space__xs));
  padding: 2px var(--space__xs);
  border: none;
  border-radius: var(--border-radius__sm);
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  &:hover {
    background: var(--color__gray-100);
  }
`;

const ImpactLabel = styled.div`
  color: var(--color__gray-900);
  font-size: var(--font-size__sm);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Impact descriptions are stored lowercase ("physical diseases"); show them
     sentence-cased without mutating the data. */
  &::first-letter {
    text-transform: uppercase;
  }
`;

const BarWrap = styled.div`
  width: 100%;
  height: 6px;
  background: var(--color__gray-100);
  border-radius: var(--border-radius__full);
  overflow: hidden;
`;

const Bar = styled.div<{ $valence: FinalScoreValence }>`
  height: 100%;
  background: ${p => (p.$valence === Valence.POSITIVE ? 'var(--color__teal-700)' : 'var(--color__purple-700)')};
  border-radius: inherit;
`;

/* Value-chain split fill (PLT-5382): outer width is the same leader-normalised
   length as `Bar`; inner segments divide it by value-chain magnitude, colored
   via `getValueChainFill` (same palette as the main impact chart). */
const BarStack = styled.div`
  display: flex;
  height: 100%;
  border-radius: inherit;
  overflow: hidden;
`;

const BarSegment = styled.div`
  height: 100%;
`;

/* Figures column mirrors the contributing-products `ProductFigures`: a bold
   share-% line plus an invisible second line that reserves the c/$ row's
   height, so Top impacts rows line up with the rows above. */
/* Positioning context for the absolutely-centred %, sized by the hidden sizer. */
const ImpactFigures = styled.div`
  position: relative;
  font-variant-numeric: tabular-nums;
`;

/* Invisible two-line stack that reserves exactly the contributing-products
   figures height (share-% line + c/$ line), so it uses the same fonts/metrics
   and the row heights match to the pixel. */
const FiguresSizer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
  visibility: hidden;
`;

/* The visible counterpart of `FiguresSizer`, used when the second line shows
   the share's 80% interval. */
const FiguresStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
`;

const ImpactPct = styled.div`
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
  white-space: nowrap;
`;

const ImpactCpsSpacer = styled.div`
  font-size: var(--font-size__xs);
  margin-top: 1px;
`;

/* The visible % — centred over the reserved two-line box so it aligns with the
   single-line label and the bar in the same row. */
const ImpactPctCentered = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
  white-space: nowrap;
`;

/* Footer beneath the list: the show-more toggle on the left, the subset
   trigger pushed to the right. Either can be absent. */
const FooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__sm);
`;

/* Right-aligns the subset trigger whether or not the show-more button is
   present, and recolors the (text) Button to gray via its color token. */
const SubsetBannerSlot = styled.div`
  margin-left: auto;
  --ds-button-color: var(--color__gray-600);
  /* Drop the text-button's horizontal padding so its right edge sits flush
     with the list/table edge (it's pushed right by margin-left: auto). */
  --ds-button-padding-inline: 0;
`;

const ShowMore = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  color: var(--color__gray-900);
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
